import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import { Select } from "../../../components/Select";
import { useI18n } from "../../../lib/i18n";
import { ApiError, brands } from "../../../lib/api";
import { COUNTRIES, LANGUAGES, TIMEZONES, detectTimezone } from "../../../lib/brand-options";
import { CATEGORIES, INDUSTRIES, suggestionsFor } from "../../../lib/industry-options";

// Slug rule mirrors server-side: 2..32 chars, lowercase alnum + hyphen,
// no leading/trailing hyphen. We pre-validate to avoid a round-trip.
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/;

function NewBrand() {
  const { t, locale } = useI18n();
  const nav = useNavigate();

  // Sensible defaults so the form is mostly pre-filled. Country comes from
  // the dashboard locale (best guess); user can change. Timezone is the
  // browser's. Primary language defaults to current UI locale.
  const [legalName, setLegalName] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [slug, setSlug] = useState("");
  const [country, setCountry] = useState(locale === "tr" ? "TR" : "US");
  const [primaryLanguage, setPrimaryLanguage] = useState(locale === "tr" ? "tr-TR" : "en-US");
  const [timezone, setTimezone] = useState(detectTimezone());
  const [industry, setIndustry] = useState("");
  const [category, setCategory] = useState("");
  const [commentsMode, setCommentsMode] = useState<"approval" | "auto">("approval");
  const [aiDisclosure, setAiDisclosure] = useState(false);
  const [holidayPosts, setHolidayPosts] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [errs, setErrs] = useState<Record<string, string>>({});

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!legalName.trim()) next.legal_name = t("brands.field.legalName");
    if (!tradeName.trim()) next.trade_name = t("brands.field.tradeName");
    if (!SLUG_RE.test(slug)) next.slug = t("brands.field.slugHint");
    if (!country) next.country = t("brands.field.country");
    if (!primaryLanguage) next.primary_language = t("brands.field.primaryLanguage");
    if (!timezone) next.timezone = t("brands.field.timezone");
    setErrs(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const b = await brands.create({
        legal_name: legalName.trim(),
        trade_name: tradeName.trim(),
        slug,
        country,
        primary_language: primaryLanguage,
        timezone,
        industry: industry.trim() || undefined,
        category: category.trim() || undefined,
        comments_mode: commentsMode,
        ai_disclosure: aiDisclosure,
        include_holiday_posts: holidayPosts,
      });
      void nav({ to: "/app/brands/$brandId", params: { brandId: b.id }, replace: true });
    } catch (e2) {
      if (e2 instanceof ApiError && e2.status === 409) {
        setErrs({ slug: t("brands.slugTaken") });
      } else if (e2 instanceof ApiError && e2.status === 400) {
        setErrs({ form: e2.message });
      } else {
        setErrs({ form: t("common.errorGeneric") });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12 flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <span className="eyebrow">{t("brands.eyebrow")}</span>
        <h1 className="font-display text-3xl font-semibold">{t("brands.create.title")}</h1>
        <p className="text-secondary text-sm">{t("brands.create.subtitle")}</p>
      </div>

      <Card>
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <Input
            label={t("brands.field.legalName")}
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            hint={t("brands.field.legalNameHint")}
            errorText={errs.legal_name ?? null}
            maxLength={200}
            autoFocus
          />
          <Input
            label={t("brands.field.tradeName")}
            value={tradeName}
            onChange={(e) => setTradeName(e.target.value)}
            hint={t("brands.field.tradeNameHint")}
            errorText={errs.trade_name ?? null}
            maxLength={200}
          />
          <Input
            label={t("brands.field.slug")}
            value={slug}
            onChange={(e) =>
              setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
            }
            hint={t("brands.field.slugHint")}
            errorText={errs.slug ?? null}
            pattern="[a-z0-9][a-z0-9-]{0,30}[a-z0-9]?"
            maxLength={32}
          />

          <div className="grid sm:grid-cols-2 gap-4">
            <Select
              label={t("brands.field.country")}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              options={COUNTRIES}
              errorText={errs.country ?? null}
            />
            <Select
              label={t("brands.field.primaryLanguage")}
              value={primaryLanguage}
              onChange={(e) => setPrimaryLanguage(e.target.value)}
              options={LANGUAGES}
              errorText={errs.primary_language ?? null}
            />
          </div>

          <Select
            label={t("brands.field.timezone")}
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            options={TIMEZONES}
            errorText={errs.timezone ?? null}
          />

          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label={t("brands.field.industry")}
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              hint={t("brands.field.industryHint")}
              suggestions={suggestionsFor(INDUSTRIES, locale)}
              autoComplete="off"
            />
            <Input
              label={t("brands.field.category")}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              hint={t("brands.field.categoryHint")}
              suggestions={suggestionsFor(CATEGORIES, locale)}
              autoComplete="off"
            />
          </div>

          <Select
            label={t("brands.field.commentsMode")}
            value={commentsMode}
            onChange={(e) => setCommentsMode(e.target.value as "approval" | "auto")}
            options={[
              { value: "approval", label: t("brands.field.commentsMode.approval") },
              { value: "auto", label: t("brands.field.commentsMode.auto") },
            ]}
          />

          {/* Two toggles styled as labelled rows so they live with the form. */}
          <ToggleRow
            label={t("brands.field.aiDisclosure")}
            hint={t("brands.field.aiDisclosureHint")}
            value={aiDisclosure}
            onChange={setAiDisclosure}
          />
          <ToggleRow
            label={t("brands.field.holidayPosts")}
            value={holidayPosts}
            onChange={setHolidayPosts}
          />

          {errs.form ? <p className="text-error text-sm" role="alert">{errs.form}</p> : null}

          <div className="flex items-center justify-between gap-3 mt-2">
            <Link to="/app/brands" className="text-sm text-secondary hover:text-primary ring-nexus rounded">
              {t("brands.detail.cancel")}
            </Link>
            <Button type="submit" loading={submitting}>{t("brands.create.submit")}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// Inline toggle — checkbox styled as a pill. Cheap enough to inline; not
// reused elsewhere yet so doesn't belong in components/.
function ToggleRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-4 cursor-pointer">
      <div className="flex flex-col gap-1">
        <span className="text-sm text-primary">{label}</span>
        {hint ? <span className="text-xs text-muted">{hint}</span> : null}
      </div>
      <span
        className={`ring-nexus relative inline-flex h-6 w-11 shrink-0 rounded-full border transition ${
          value ? "bg-red border-transparent" : "bg-elevated border-border-default"
        }`}
        tabIndex={0}
        role="switch"
        aria-checked={value}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            onChange(!value);
          }
        }}
      >
        <input
          type="checkbox"
          className="sr-only"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span
          aria-hidden
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
            value ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
    </label>
  );
}

export const Route = createFileRoute("/app/brands/new")({
  component: NewBrand,
});
